import { primitives } from "@baton/tokens";

/**
 * The motion tokens a script reads: a duration as the document resolves it now, and an easing's
 * curve. Apart from `pixels.ts`, which follows any reference through the token resolver and the
 * component layer, because the public entry draws the skeleton and the key hint inside its budget
 * (FR-015-120) and the component layer is a module of its own it does not otherwise carry; the four
 * durations and the three easings are primitives, so the lookup is one step.
 */
function tokenValue(name: string): string {
  const value = primitives[name];
  if (value === undefined || typeof value !== "string") throw new Error(`--${name} is not a motion primitive`);
  return value;
}

/**
 * A duration token in milliseconds as the document resolves it now: the reduced-motion override of
 * the token stylesheet sets every duration to 0 ms, and a script that times a fade or a drop's settle
 * (§ Motion) has to read that resolved value rather than the token's own, else it would wait out a
 * fade that never ran. Where no document holds the stylesheet (a test), the token's static value stands.
 * The value is read in every form CSS writes a time in: the built stylesheet's minifier writes the
 * token's `150ms` as `.15s`, which a pattern that wanted a digit before the point refused, so every
 * reader threw in a production build (found by the gallery run, 2026-10-23).
 */
export function durationMs(name: string): number {
  const live = typeof document === "undefined" ? "" : getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  return parseDuration(live.length > 0 ? live : tokenValue(name), name);
}

/** A CSS time, `150ms`, `0.15s`, `.15s` or `0s`, in milliseconds. */
function parseDuration(value: string, name: string): number {
  const match = /^(\d*\.?\d+)(ms|s)$/.exec(value);
  if (match?.[1] === undefined) throw new Error(`--${name} is not a duration: ${value}`);
  return Number(match[1]) * (match[2] === "s" ? 1000 : 1);
}

/** An easing token's curve as a script hands it to an animation (a drop's settle); the easings are never zeroed, so the token's own value stands. */
export function easingValue(name: string): string {
  return tokenValue(name);
}
